import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { metricsService } from '../../services/endpoints/metricsService';
import { DailyMetrics } from '../../types';
import CoachCommentThread from './CoachCommentThread';
import CheckInPhotoSection from '../metrics/CheckInPhotoSection';

interface Props {
  athleteUserId: string;
  athleteName: string;
  relationshipId: string;
  onClose: () => void;
}

const AthleteCheckInDrawer: React.FC<Props> = ({
  athleteUserId,
  athleteName,
  relationshipId,
  onClose,
}) => {
  const [metrics, setMetrics]       = useState<DailyMetrics | null>(null);
  const [loading, setLoading]       = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [noData, setNoData]         = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setFetchError(null);
        setNoData(false);
        const data = await metricsService.getLatestMetrics(athleteUserId);
        if (!cancelled) setMetrics(data);
      } catch (err: any) {
        if (!cancelled) {
          if (err?.response?.status === 404) {
            setNoData(true);
          } else {
            setFetchError('Failed to load check-in data.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [athleteUserId]);

  const encodedName = encodeURIComponent(athleteName);
  const metricsUrl  = `/metrics?viewAs=${athleteUserId}&viewAsName=${encodedName}&relationshipId=${relationshipId}`;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-dark-800 border-l border-dark-700 z-50 flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-dark-700 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">Latest Check-In</h2>
            <p className="text-sm text-gray-400 mt-0.5">{athleteName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 transition-colors text-xl leading-none mt-0.5"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {loading ? (
            <p className="text-gray-500 text-sm text-center py-6">Loading check-in data…</p>
          ) : fetchError ? (
            <p className="text-red-400 text-sm text-center py-6">{fetchError}</p>
          ) : noData || !metrics ? (
            <p className="text-gray-500 text-sm text-center py-6">No check-in data yet.</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {new Date(metrics.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
                <Link to={metricsUrl} className="text-xs text-gold-500 hover:text-gold-400 transition-colors">
                  View All Check-Ins →
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <MetricTile label="Weight"    value={metrics.weight != null ? `${metrics.weight} lbs` : null} />
                <MetricTile
                  label="Wellbeing"
                  value={metrics.wellbeingScore != null ? `${metrics.wellbeingScore} / 10` : null}
                  valueColor={wellbeingColor(metrics.wellbeingScore)}
                />
                <MetricTile
                  label="Blood Pressure"
                  value={
                    metrics.bloodPressureSystolic != null && metrics.bloodPressureDiastolic != null
                      ? `${metrics.bloodPressureSystolic} / ${metrics.bloodPressureDiastolic}`
                      : metrics.bloodPressureSystolic != null
                      ? `${metrics.bloodPressureSystolic} / —`
                      : null
                  }
                  valueColor={bpColor(metrics.bloodPressureSystolic, metrics.bloodPressureDiastolic)}
                />
                <MetricTile
                  label="Glucose"
                  value={metrics.glucoseLevel != null ? `${metrics.glucoseLevel} mg/dL` : null}
                  valueColor={glucoseColor(metrics.glucoseLevel)}
                />
                <MetricTile label="HRV"   value={metrics.overnightHrv != null ? String(metrics.overnightHrv) : null} />
                <MetricTile label="Sleep" value={metrics.sleepHours != null ? `${metrics.sleepHours} h` : null} />
              </div>

              {metrics.notes && (
                <div className="bg-dark-700 rounded-xl p-4">
                  <p className="text-xs font-medium text-gray-400 mb-1.5">Athlete Notes</p>
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">{metrics.notes}</p>
                </div>
              )}

              <CheckInPhotoSection
                metricId={metrics.metricId}
                viewAs={athleteUserId}
                readOnly={true}
              />

              <CoachCommentThread
                contextType="DailyCheckIn"
                contextId={metrics.metricId}
                label="check-in"
                defaultExpanded={true}
                forceRelationshipId={relationshipId}
                forceViewAsName={athleteName}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface TileProps {
  label: string;
  value: string | null;
  valueColor?: string;
}

const MetricTile: React.FC<TileProps> = ({ label, value, valueColor = 'text-white' }) => (
  <div className="bg-dark-700 rounded-lg px-3 py-2.5">
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className={`text-sm font-semibold ${value ? valueColor : 'text-gray-600'}`}>
      {value ?? '—'}
    </p>
  </div>
);

// ── Color helpers ─────────────────────────────────────────────────────────────

function wellbeingColor(score?: number): string {
  if (score == null) return 'text-white';
  if (score >= 7) return 'text-green-400';
  if (score >= 5) return 'text-yellow-400';
  return 'text-red-400';
}

function bpColor(systolic?: number, diastolic?: number): string {
  if (systolic == null) return 'text-white';
  if (systolic >= 140 || (diastolic != null && diastolic >= 90)) return 'text-red-400';
  if (systolic >= 130 || (diastolic != null && diastolic >= 80)) return 'text-yellow-400';
  return 'text-white';
}

function glucoseColor(level?: number): string {
  if (level == null) return 'text-white';
  if (level > 100) return 'text-yellow-400';
  return 'text-white';
}

export default AthleteCheckInDrawer;
